import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApiCaller } from '../utils/ApiUtilities';
import {
    getCustomQuizGetByIdUrl,
    getPublicGradeUrl,
    getFavoriteQuestionPostUrl,
    getFavoriteQuestionDeleteUrl,
} from '../utils/UrlUtilities';
import { constructPublicGradePayload, getFavoriteQuestionPostPayload } from '../utils/PayloadUtils';
import { buildSelectedOptionsJson } from '../utils/BasicUtilities';
import QuizQuestionCard from './QuizQuestionCard';
import MathText from './ui/MathText';
import CloseButton from './ui/CloseButton';

// Self-paced practice for a topic-wise micro quiz: renders the quiz's own questions with the same
// reveal UX as the homepage (LandingPractice) — pick an option, then reveal the correct answer +
// explanation on demand. No timer, no submission (nothing is recorded). Fed by the quiz's questions
// (getCustomQuizGetByIdUrl); correct answers/explanations are fetched lazily via the public grade
// endpoint, exactly like LandingPractice.
const QuizPracticeInterface = ({ quiz, onClose }) => {
    const CallApi = useApiCaller();
    const quizId = quiz?.quiz_id;

    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({});           // qid -> selection (shape per QuizQuestionCard)
    const [revealedIds, setRevealedIds] = useState(new Set());
    const [answersById, setAnswersById] = useState({});     // qid -> {correct_option, correct_text_answer, answer_explanation}
    const [revealLoading, setRevealLoading] = useState(false);
    const [favorites, setFavorites] = useState(new Set());
    const answersLoadedRef = useRef(false);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        if (!quizId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            const res = await CallApi({ url: getCustomQuizGetByIdUrl(quizId), method: 'GET', token: true });
            if (!cancelled) {
                const bySubject = res.success ? (res.data?.questions || {}) : {};
                setQuestions(Object.values(bySubject).flat());
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizId]);

    const handleSelect = (qid, opt) => setSelected(prev => ({ ...prev, [qid]: opt }));
    const handleToggle = (qid, opt) => setSelected(prev => {
        const cur = Array.isArray(prev[qid]) ? prev[qid] : [];
        return { ...prev, [qid]: cur.includes(opt) ? cur.filter(o => o !== opt) : [...cur, opt] };
    });
    const handleBlank = (qid, bi, ci) => setSelected(prev => {
        const cur = (prev[qid] && prev[qid].blanks) || {};
        return { ...prev, [qid]: { blanks: { ...cur, [bi]: ci } } };
    });
    const handleText = (qid, text) => setSelected(prev => ({ ...prev, [qid]: { text } }));
    const handleOrder = (qid, order) => setSelected(prev => ({ ...prev, [qid]: { order } }));

    const handleToggleFavorite = useCallback(async (qid) => {
        const isFav = favorites.has(qid);
        // Optimistic flip; revert on failure.
        setFavorites(prev => { const n = new Set(prev); isFav ? n.delete(qid) : n.add(qid); return n; });
        const res = isFav
            ? await CallApi({ url: getFavoriteQuestionDeleteUrl([qid]), method: 'DELETE', token: true })
            : await CallApi({ url: getFavoriteQuestionPostUrl(), method: 'POST', payload: getFavoriteQuestionPostPayload(qid), token: true });
        if (!res.success) {
            setFavorites(prev => { const n = new Set(prev); isFav ? n.add(qid) : n.delete(qid); return n; });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favorites]);

    // Load answers for the whole set once (grade endpoint), cache, so later reveals need no fetch.
    const ensureAnswersLoaded = async () => {
        if (answersLoadedRef.current) return true;
        const merged = {};
        questions.forEach(q => { merged[q.question_id] = selected[q.question_id] ?? ''; });
        const payload = constructPublicGradePayload(buildSelectedOptionsJson(merged));
        setRevealLoading(true);
        const res = await CallApi({ url: getPublicGradeUrl(), method: 'POST', payload, token: false });
        setRevealLoading(false);
        if (!res.success) return false;
        const map = {};
        (res.data?.questions || []).forEach(gq => {
            map[gq.question_id] = {
                correct_option: gq.correct_option,
                correct_text_answer: gq.correct_text_answer,
                answer_explanation: gq.answer_explanation,
            };
        });
        setAnswersById(map);
        answersLoadedRef.current = true;
        return true;
    };

    const handleRevealOne = async (qid) => {
        const ok = await ensureAnswersLoaded();
        if (!ok) return;
        setRevealedIds(prev => { const n = new Set(prev); n.add(qid); return n; });
    };
    const handleHideOne = (qid) => setRevealedIds(prev => { const n = new Set(prev); n.delete(qid); return n; });

    return (
        <div style={{ backgroundColor: '#ffffff', borderRadius: '0.9rem', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '95%', maxWidth: 'min(900px, 96vw)', height: 'min(88vh, 780px)', maxHeight: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff', padding: '1.1rem 1.25rem', position: 'relative', flexShrink: 0 }}>
                <CloseButton onClick={onClose} />
                <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, paddingRight: '2rem' }}>{quiz?.title}</h2>
                <p style={{ margin: '0.35rem 0 0', opacity: 0.85, fontSize: '0.85rem' }}>
                    Practice mode — pick an option, then reveal the correct answer &amp; explanation. Untimed; nothing is recorded.
                </p>
            </div>

            <div style={{ overflowY: 'auto', padding: '1.25rem', flex: 1 }}>
                {loading ? (
                    <p className="text-muted">Loading questions…</p>
                ) : questions.length === 0 ? (
                    <p className="text-muted">No questions available.</p>
                ) : questions.map((q, idx) => {
                    const isRevealed = revealedIds.has(q.question_id);
                    const answer = answersById[q.question_id];
                    return (
                        <div key={q.question_id} className="mb-3 pb-2 border-bottom">
                            <div style={isRevealed ? { pointerEvents: 'none', opacity: 0.95 } : undefined}>
                                <QuizQuestionCard
                                    question={q}
                                    qIndex={idx}
                                    includePassage
                                    answer={selected[q.question_id]}
                                    isAnswered={selected[q.question_id] != null}
                                    isFavorite={favorites.has(q.question_id)}
                                    onOptionSelect={handleSelect}
                                    onMultiToggle={handleToggle}
                                    onBlankSelect={handleBlank}
                                    onTextChange={handleText}
                                    onOrderChange={handleOrder}
                                    onToggleFavorite={handleToggleFavorite}
                                />
                            </div>
                            {isRevealed && answer && (
                                <div className="alert alert-success py-2 mb-2 kp-read-explanation">
                                    <div>
                                        <strong>Correct answer:</strong>{' '}
                                        {answer.correct_text_answer
                                            ? <MathText text={answer.correct_text_answer} />
                                            : (answer.correct_option ? <MathText text={answer.correct_option} /> : '—')}
                                    </div>
                                    {answer.answer_explanation && (
                                        <div className="mt-1"><strong>Why:</strong> <MathText text={answer.answer_explanation} /></div>
                                    )}
                                </div>
                            )}
                            {isRevealed ? (
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => handleHideOne(q.question_id)}>
                                    Hide answer &amp; explanation
                                </button>
                            ) : (
                                <button className="btn btn-sm btn-outline-warning" onClick={() => handleRevealOne(q.question_id)} disabled={revealLoading}>
                                    {revealLoading ? 'Loading…' : 'Show result & explanation'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
        </div>
    );
};

export default QuizPracticeInterface;
